
import React from 'react';

export const ProcessSection: React.FC = () => {
  const processSteps = [
    {
      step: '1단계',
      title: '사업계획 수립',
      description: '지역 현황 분석 및 개발 방향 설정',
      status: 'completed',
      details: ['현황 조사', '기본계획 수립', '주민 설명회']
    },
    {
      step: '2단계',
      title: '추진위원회 구성',
      description: '주민 대표로 구성된 추진위원회 설립',
      status: 'completed',
      details: ['추진위 구성', '임원 선출', '운영 규정 제정']
    },
    {
      step: '3단계',
      title: '정비구역 지정',
      description: '관할 지자체의 정비구역 지정 승인',
      status: 'current',
      details: ['지정 신청', '심의 진행', '고시 예정']
    },
    {
      step: '4단계',
      title: '조합 설립',
      description: '정비사업조합 설립인가 추진',
      status: 'pending',
      details: ['조합 설립', '인가 신청', '조합원 확정']
    },
    {
      step: '5단계',
      title: '사업시행인가',
      description: '구체적인 사업계획 승인 및 시행',
      status: 'pending',
      details: ['시행계획 수립', '인가 신청', '착공 준비']
    },
    {
      step: '6단계',
      title: '준공 및 입주',
      description: '건설 완료 후 입주 및 사업 완료',
      status: 'pending',
      details: ['건설 진행', '준공 검사', '입주 시작']
    }
  ];

  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 text-white border-green-500';
      case 'current':
        return 'bg-blue-600 text-white border-blue-600';
      case 'pending':
        return 'bg-gray-200 text-gray-600 border-gray-300';
      default:
        return 'bg-gray-200 text-gray-600 border-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return 'ri-check-line';
      case 'current':
        return 'ri-play-circle-line';
      case 'pending':
        return 'ri-time-line';
      default:
        return 'ri-time-line';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return '완료';
      case 'current':
        return '진행중';
      case 'pending':
        return '예정';
      default:
        return '예정';
    }
  };

  return (
    <section id="process" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            모아타운 사업 진행 절차
          </h2>
          <p className="text-lg text-gray-600">
            대청마을 북측구역 통합개발 사업의 단계별 진행 절차를 안내해드립니다
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 relative">
                {/* 단계 번호 */}
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full border-2 mb-4 ${getStepColor(step.status)}`}>
                  <i className={`${getStatusIcon(step.status)} text-xl`}></i>
                </div>

                {/* 단계 정보 */}
                <div className="mb-4">
                  <div className="text-sm font-medium text-blue-600 mb-1">{step.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{step.description}</p>
                </div>

                {/* 상태 표시 */}
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-4 ${
                  step.status === 'completed' ? 'bg-green-100 text-green-700' :
                  step.status === 'current' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {getStatusText(step.status)}
                </div>

                {/* 세부 항목 */}
                <div className="space-y-2">
                  {step.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="flex items-center text-sm text-gray-600">
                      <div className={`w-2 h-2 rounded-full mr-2 ${
                        step.status === 'completed' ? 'bg-green-400' :
                        step.status === 'current' ? 'bg-blue-400' :
                        'bg-gray-300'
                      }`}></div>
                      {detail}
                    </div>
                  ))}
                </div>

                {/* 연결선 (데스크톱에서만) */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-2xl text-gray-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 현재 진행 상황 요약 */}
          <div className="bg-blue-50 rounded-lg p-6 mt-12">
            <div className="flex items-center mb-4">
              <i className="ri-information-line text-blue-600 text-xl mr-2"></i>
              <h3 className="text-lg font-bold text-blue-900">현재 진행 상황</h3>
            </div>
            <div className="text-blue-800">
              <p className="mb-2">
                <strong>현재 단계:</strong> 정비구역 지정 단계 진행 중
              </p>
              <p className="mb-2">
                <strong>예상 일정:</strong> 2026년 정비구역 지정 완료 예상
              </p>
              <p className="text-sm text-blue-600">
                ※ 일정은 관련 기관의 심의 및 승인 과정에 따라 변동될 수 있습니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
